import { LayoutSizeContext } from 'App';
import AddWidgetComponent from 'components/AddWidgetComponent';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import Widget from 'components/Widget';
import {
  DEFAULT_LOCATION,
  LOCATION_PERMISSION_DISABLED_MESSAGE,
} from 'config/defaultLocation.config.js';
import { usePerms } from 'contexts/PermsContext';
import GetWidgetData from 'data/widgetData/widgetData.js';
import WidgetSelector from 'features/dashboard/features/WidgetSelector';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { toast } from 'react-toastify';
// import { useGetPendingAlarmsQuery } from 'services/alarms/alarms.api'; // update to v2 if needed
import useLocation from 'hooks/useLocation';
import { useSelector } from 'react-redux';
import { useGetAlarmsQuery } from 'services/Alarms/Alarms.api';
import { useGetAllActiveAlertsQuery } from 'services/alerts/alerts.api';
import { useGetRoleQuery } from 'services/roles/roles.api';
import {
  useGetUserWidgetDataQuery,
  useUpdateUserWidgetLayoutMutation,
} from 'services/user/widgets/widgets.api';
import { selectUsername } from 'store/selectors/User/userSelectors';
import { safeJSONParse } from 'utils/data/parse.utils';
import { addViewportResizeListeners } from 'utils/layout/viewportResize.utils';
import './assets/Dashboard.css';
import TotalAlertCard from './TotalAlertCard';
const ResponsiveGridLayout = WidthProvider(Responsive);
const DASHBOARD_COLS_BY_BREAKPOINT = { xs: 1, sm: 1, md: 1, lg: 3, xl: 4, xxl: 4 };

//TODO: Update devices. rise rate threshold should say "Rise Rate" instead of "Flooding", and drop rate threshold should say "Drop Rate" instead of "Flooding".

//TODO: Add signal strenght to battery graph for aware devices. It will be a second line on the same graph.

export default function Dashboard({ search, definedWidgetData, accessLevel }) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('Dashboard');
    }
  } catch (e) {}
  const { data: userWidgets, isFetching: userWidgetsFetching } = useGetUserWidgetDataQuery();
  const [updateUserWidgetLayout] = useUpdateUserWidgetLayoutMutation();
  const username = useSelector(selectUsername);
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const [bp, setBp] = useState('xxl');
  const [list, setList] = useState([]);
  const [choiceList, setChoiceList] = useState(null);
  const userListRef = useRef([]);
  const [weatherAlertCount, setWeatherAlertCount] = useState(0);
  const [fusionAlertCount, setFusionAlertCount] = useState(0);
  const [canMoveWidgets, setCanMoveWidgets] = useState(false);
  const [canEditWidgets, setCanEditWidgets] = useState(false);
  const perms = usePerms();
  const layout = useContext(LayoutSizeContext);
  const [widgetSelectorOpen, setWidgetSelectorOpen] = useState(false);
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);
  const [locationPermissionNotice, setLocationPermissionNotice] = useState('');
  const dashboardGridRef = useRef(null);
  const [dashboardRowHeight, setDashboardRowHeight] = useState(layout.widgetHeight);

  const canViewAlerts = perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewAlerts']);
  const canViewAlarms = perms.testPermission([8, 9, 10, 11, 12, 13], ['PendingAlarms']);
  const canViewRoles = perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewRoles']);
  const canViewUsers = perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewUsers']);

  const userRole = perms.roleId !== null ? parseInt(perms.roleId) : -1;
  const { data: roleData } = useGetRoleQuery(userRole, { skip: !canViewRoles || userRole <= -1 });

  const { data: allActiveAlerts = [] } = useGetAllActiveAlertsQuery(undefined, {
    skip: !canViewAlerts,
  });
  const { data: alarmsData } = useGetAlarmsQuery({ state: 1 }, { skip: !canViewAlarms });

  let totalAlarmsCount = '';
  if (!(perms.isFetching || perms.data === null)) {
    totalAlarmsCount = canViewAlarms ? (alarmsData?.meta?.resultCount ?? 0) : 'Unavailable';
  }
  const canOverrideLockedFromRole = perms.testPermission([11, 12, 13], []);
  const showAnalyticHeaders = perms.testPermission([8, 9, 10, 11, 12, 13], ['AnalyticHeaders']);
  const removableWidgetCount = (list || []).filter(
    (item) => !(item?.lockedFromRole === true && !canOverrideLockedFromRole),
  ).length;
  const lockedRoleWidgetCount = (list || []).filter(
    (item) => item?.lockedFromRole === true && !canOverrideLockedFromRole,
  ).length;

  const collidesLayoutItems = (a, b) =>
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

  const isMapWidgetDefinition = (widgetDefinition) => {
    if (!widgetDefinition) return false;

    if (widgetDefinition.contentType === 'map') return true;

    if (Array.isArray(widgetDefinition.category) && widgetDefinition.category.includes('Maps')) {
      return true;
    }

    const combinedLabel = `${widgetDefinition.name || ''} ${widgetDefinition.displayName || ''}`;
    return /map/i.test(combinedLabel);
  };

  const canWidgetUseExpandedMapSizing = (widgetDefinition) =>
    isMapWidgetDefinition(widgetDefinition);

  const isChartOrGraphWidgetDefinition = (widgetDefinition) => {
    if (!widgetDefinition || isMapWidgetDefinition(widgetDefinition)) return false;

    if (Array.isArray(widgetDefinition.category) && widgetDefinition.category.includes('Charts')) {
      return true;
    }

    const combinedLabel = `${widgetDefinition.name || ''} ${widgetDefinition.displayName || ''}`;
    return /chart/i.test(combinedLabel);
  };

  const canWidgetUseExpandedChartSizing = (widgetDefinition) =>
    isChartOrGraphWidgetDefinition(widgetDefinition);

  const isConnectedDevicesListWidgetDefinition = (widgetDefinition) =>
    widgetDefinition?.name === 'ConnectedDevices';

  const getOverwatchWidgetResizeProfile = (widgetDefinition, cols) => {
    const name = widgetDefinition?.name;
    if (!name) return null;

    // Connected Devices list + similar device management widgets:
    // allow wider layouts while keeping the tall (1x2) behavior.
    const tallTwoRowNames = new Set([
      'ConnectedDeviceGroups',
      'TechnologyBridgeDevices',
      'ManualRoadClosureDevices',
      'TechnologyBridgeSirenDevices',
    ]);

    if (tallTwoRowNames.has(name)) {
      return {
        minW: 1,
        maxW: Math.min(2, cols),
        minH: 1,
        maxH: 2,
        resizeHandles: ['e', 's', 'se'],
        disallowOneByOne: true,
      };
    }

    if (name === 'OverwatchVMS') {
      return {
        minW: Math.min(2, cols),
        maxW: Math.min(4, cols),
        minH: 1,
        maxH: 2,
        resizeHandles: ['e', 's', 'se'],
        disallowOneByOne: false,
      };
    }

    return null;
  };

  const buildGridItemLayout = ({ item, index, cols, forceSingleColumnWidth = false }) => {
    const rawW = Math.max(1, Number(item?.w) || 1);
    const rawH = Math.max(1, Number(item?.h) || 1);
    const widgetDefinition = definedWidgetData?.[item?.id];
    const canEditAtBreakpoint = canEditWidgets && cols > 1;
    const mapWidget = canWidgetUseExpandedMapSizing(widgetDefinition);
    const chartWidget = canWidgetUseExpandedChartSizing(widgetDefinition);
    const overwatchResizeProfile = getOverwatchWidgetResizeProfile(widgetDefinition, cols);
    const mapResizable = canEditAtBreakpoint && (item.isDraggable ?? true) && mapWidget;
    const chartResizable = canEditAtBreakpoint && (item.isDraggable ?? true) && chartWidget;
    const connectedDevicesListResizable =
      canEditAtBreakpoint &&
      (item.isDraggable ?? true) &&
      isConnectedDevicesListWidgetDefinition(widgetDefinition);
    const overwatchProfileResizable =
      canEditAtBreakpoint && (item.isDraggable ?? true) && Boolean(overwatchResizeProfile);

    const maxChartWidth = Math.min(4, cols);
    const minChartWidth = Math.min(2, cols);
    const maxConnectedDevicesListWidth = Math.min(2, cols);
    const minConnectedDevicesListWidth = 1;
    let normalizedW = forceSingleColumnWidth
      ? 1
      : overwatchProfileResizable
        ? Math.max(overwatchResizeProfile.minW, Math.min(overwatchResizeProfile.maxW, rawW))
        : connectedDevicesListResizable
          ? Math.max(minConnectedDevicesListWidth, Math.min(maxConnectedDevicesListWidth, rawW))
          : chartWidget
            ? Math.max(minChartWidth, Math.min(maxChartWidth, rawW))
            : Math.max(1, Math.min(cols, rawW));
    let normalizedH = connectedDevicesListResizable
      ? 2
      : overwatchProfileResizable
        ? Math.max(overwatchResizeProfile.minH, Math.min(overwatchResizeProfile.maxH, rawH))
        : chartWidget
          ? Math.max(1, Math.min(2, rawH))
          : Math.max(1, Math.min(2, rawH));

    if (
      overwatchProfileResizable &&
      overwatchResizeProfile.disallowOneByOne &&
      normalizedW === 1 &&
      normalizedH === 1
    ) {
      normalizedH = 2;
    }
    const isResizable =
      mapResizable || chartResizable || connectedDevicesListResizable || overwatchProfileResizable;

    return {
      i: String(index),
      x: item.x ?? 0,
      y: item.y ?? 0,
      w: normalizedW,
      h: normalizedH,
      isDraggable: canEditAtBreakpoint && (item.isDraggable ?? true),
      isResizable,
      resizeHandles: overwatchProfileResizable
        ? overwatchResizeProfile.resizeHandles
        : connectedDevicesListResizable
          ? ['e', 'se']
          : isResizable
            ? ['e', 's', 'se']
            : ['se'],
      minW: mapResizable
        ? 1
        : overwatchProfileResizable
          ? overwatchResizeProfile.minW
          : connectedDevicesListResizable
            ? minConnectedDevicesListWidth
            : chartResizable
              ? minChartWidth
              : normalizedW,
      maxW: mapResizable
        ? cols
        : overwatchProfileResizable
          ? overwatchResizeProfile.maxW
          : connectedDevicesListResizable
            ? maxConnectedDevicesListWidth
            : chartResizable
              ? maxChartWidth
              : normalizedW,
      minH: mapResizable
        ? 1
        : overwatchProfileResizable
          ? overwatchResizeProfile.minH
          : connectedDevicesListResizable
            ? 2
            : chartResizable
              ? 1
              : normalizedH,
      maxH: mapResizable
        ? 2
        : overwatchProfileResizable
          ? overwatchResizeProfile.maxH
          : connectedDevicesListResizable
            ? 2
            : chartResizable
              ? 2
              : normalizedH,
    };
  };

  const findNextGridPlacement = (items, cols, w = 1, h = 1) => {
    const normalizedWidth = Math.max(1, Math.min(cols, Number(w) || 1));
    const normalizedHeight = Math.max(1, Number(h) || 1);
    const maxRow = Math.max(
      0,
      ...items.map((item) => (Number(item.y) || 0) + (Number(item.h) || 1)),
    );

    for (let y = 0; y <= maxRow + 100; y++) {
      for (let x = 0; x <= cols - normalizedWidth; x++) {
        const candidate = { x, y, w: normalizedWidth, h: normalizedHeight };
        if (items.every((existingItem) => !collidesLayoutItems(existingItem, candidate))) {
          return { x, y };
        }
      }
    }

    return { x: 0, y: maxRow + 1 };
  };

  const repackLayoutItemsForEdgeCase = (items, cols) => {
    if (!Array.isArray(items) || cols <= 1) {
      return items;
    }

    const hasTallWidgets = items.some((item) => Number(item?.w) === 1 && Number(item?.h) === 2);
    const hasWideSingleRowWidgets = items.some(
      (item) => Number(item?.h) === 1 && Number(item?.w) > 1,
    );
    if (!hasTallWidgets || hasWideSingleRowWidgets) {
      return items;
    }

    const orderedItems = [...items].sort((a, b) => {
      const yDiff = (Number(a?.y) || 0) - (Number(b?.y) || 0);
      if (yDiff !== 0) return yDiff;

      const xDiff = (Number(a?.x) || 0) - (Number(b?.x) || 0);
      if (xDiff !== 0) return xDiff;

      return String(a?.i ?? '').localeCompare(String(b?.i ?? ''), undefined, { numeric: true });
    });

    const placedItems = [];

    return orderedItems.map((item) => {
      const normalizedWidth = Math.max(1, Math.min(cols, Number(item?.w) || 1));
      const normalizedHeight = Math.max(1, Number(item?.h) || 1);
      const { x, y } = findNextGridPlacement(placedItems, cols, normalizedWidth, normalizedHeight);

      const packedItem = {
        ...item,
        x,
        y,
        w: normalizedWidth,
        h: normalizedHeight,
      };

      placedItems.push(packedItem);
      return packedItem;
    });
  };

  const appendAddWidget = (arr) => {
    return [
      ...arr,
      {
        i: 'add',
        displayed: true,
        isDraggable: false,
        isResizable: false,
        locked: true,
        static: false,
        x: 0,
        y: Infinity,
        w: 1,
        h: 1,
      },
    ];
  };

  const createLayout = (list) => ({
    xxl: appendAddWidget(
      repackLayoutItemsForEdgeCase(
        list.map((item, index) =>
          buildGridItemLayout({
            item,
            index,
            cols: DASHBOARD_COLS_BY_BREAKPOINT.xxl,
          }),
        ),
        DASHBOARD_COLS_BY_BREAKPOINT.xxl,
      ),
    ),
    xl: appendAddWidget(
      repackLayoutItemsForEdgeCase(
        list.map((item, index) =>
          buildGridItemLayout({
            item,
            index,
            cols: DASHBOARD_COLS_BY_BREAKPOINT.xl,
          }),
        ),
        DASHBOARD_COLS_BY_BREAKPOINT.xl,
      ),
    ),
    lg: appendAddWidget(
      repackLayoutItemsForEdgeCase(
        list.map((item, index) =>
          buildGridItemLayout({
            item,
            index,
            cols: DASHBOARD_COLS_BY_BREAKPOINT.lg,
          }),
        ),
        DASHBOARD_COLS_BY_BREAKPOINT.lg,
      ),
    ),
    md: appendAddWidget(
      repackLayoutItemsForEdgeCase(
        list.map((item, index) =>
          buildGridItemLayout({
            item,
            index,
            cols: DASHBOARD_COLS_BY_BREAKPOINT.md,
            forceSingleColumnWidth: true,
          }),
        ),
        DASHBOARD_COLS_BY_BREAKPOINT.md,
      ),
    ),
    sm: appendAddWidget(
      repackLayoutItemsForEdgeCase(
        list.map((item, index) =>
          buildGridItemLayout({
            item,
            index,
            cols: DASHBOARD_COLS_BY_BREAKPOINT.sm,
            forceSingleColumnWidth: true,
          }),
        ),
        DASHBOARD_COLS_BY_BREAKPOINT.sm,
      ),
    ),
    xs: appendAddWidget(
      repackLayoutItemsForEdgeCase(
        list.map((item, index) =>
          buildGridItemLayout({
            item,
            index,
            cols: DASHBOARD_COLS_BY_BREAKPOINT.xs,
            forceSingleColumnWidth: true,
          }),
        ),
        DASHBOARD_COLS_BY_BREAKPOINT.xs,
      ),
    ),
  });

  const layouts = useMemo(() => createLayout(list ?? []), [list]);

  useEffect(() => {
    const defaultLocationText = `${DEFAULT_LOCATION.label} (${DEFAULT_LOCATION.latitude}, ${DEFAULT_LOCATION.longitude})`;
    const deniedNotice = `${LOCATION_PERMISSION_DISABLED_MESSAGE} Default location: ${defaultLocationText}.`;
    let isMounted = true;
    let permissionStatus = null;

    const updateNotice = (state) => {
      if (!isMounted) return;

      if (state === 'denied' || state === 'unavailable') {
        setLocationPermissionNotice(deniedNotice);
        return;
      }

      if (state === 'granted') {
        setLocationPermissionNotice('');
        return;
      }

      if (effectiveLocation.source === 'default') {
        setLocationPermissionNotice(
          `Using default location ${defaultLocationText} until browser location is available.`,
        );
      } else {
        setLocationPermissionNotice('');
      }
    };

    if (!navigator?.geolocation) {
      updateNotice('unavailable');
      return () => {
        isMounted = false;
      };
    }

    if (!navigator.permissions?.query) {
      updateNotice('unknown');
      return () => {
        isMounted = false;
      };
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (!isMounted) return;
        permissionStatus = result;
        updateNotice(result.state);
        permissionStatus.onchange = () => updateNotice(permissionStatus.state);
      })
      .catch(() => {
        updateNotice('unknown');
      });

    return () => {
      isMounted = false;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  useEffect(() => {
    setCanMoveWidgets(perms.testPermission([8, 9, 10, 11, 12, 13], ['WidgetsAreMovable']));
    setCanEditWidgets(perms.testPermission([8, 9, 10, 11, 12, 13], ['WidgetsAreEditable']));
  }, [perms.data]);

  // TODO: Move this into a hook or something
  useEffect(() => {
    const weatherAlerts = allActiveAlerts
      ?.filter((filter) => filter.provider === 'Custom Weather')
      .map((item) => item);

    const fusionAlerts = allActiveAlerts
      ?.filter((filter) => filter.provider === 'Fusion')
      .map((item) => item);

    setWeatherAlertCount(weatherAlerts?.length);
    setFusionAlertCount(fusionAlerts?.length);

    // Wait for perms from context to load before trying to load widgets
    if (perms.isFetching || perms.data === null) return;

    const userRole = perms.roleId !== null ? parseInt(perms.roleId) : -1;
    const widgetDefinitions = GetWidgetData();

    if (userRole > -1) {
      const filteredWidgetList = Object.entries(widgetDefinitions)
        .filter(([widgetId, widgetDefinition]) =>
          perms.testPermission([8, 9, 10, 11, 12, 13], widgetDefinition.permissions),
        )
        .map(([widgetId, widgetDefinition]) => ({
          // provide a consistent shape for choiceList entries
          ...widgetDefinition,
          display: true,
          w: widgetDefinition.w ?? 1,
          h: widgetDefinition.h ?? 1,
          isDraggable:
            widgetDefinition.isDraggable !== undefined ? widgetDefinition.isDraggable : true,
          locked: widgetDefinition.locked !== undefined ? widgetDefinition.locked : false,
          x: widgetDefinition.x ?? 0,
          y: widgetDefinition.y ?? 0,
          id: widgetId,
        }));

      setChoiceList(filteredWidgetList);
    } else {
      setChoiceList(
        Object.keys(widgetDefinitions).map((widgetId) => ({
          display: true,
          w: 1,
          h: 1,
          isDraggable: true,
          locked: false,
          x: 0,
          y: 0,
          id: widgetId,
        })),
      );
    }

    const loadWidgets = async () => {
      // Try role widgets first, otherwise fall back to user widgets
      if (userRole > -1) {
        try {
          // block is needed to ensure we don't try to load role widgets before we have the necessary permissions info from perms context
          if (canViewRoles && roleData) {
            const raw = roleData?.widget_list;
            let parsed = [];
            if (raw !== undefined && raw !== null) {
              if (typeof raw === 'string') {
                const s = raw.trim();
                const p = safeJSONParse(s, []);
                parsed = Array.isArray(p) ? p : [];
              } else if (Array.isArray(raw)) {
                parsed = raw;
              } else if (typeof raw === 'object') {
                // sometimes API may wrap layout in an object
                if (Array.isArray(raw.layout)) parsed = raw.layout;
                else parsed = [];
              } else {
                parsed = [];
              }
            }

            if (parsed && parsed.length > 0) {
              const normalized = parsed.map((it) => {
                const locked = it.locked !== undefined ? !!it.locked : it.isDraggable === false;
                return {
                  ...it,
                  locked,
                  lockedFromRole: locked === true,
                  isDraggable: it.isDraggable !== undefined ? it.isDraggable : !locked,
                };
              });

              setList([...normalized]);
              userListRef.current = [...normalized];
              return;
            }
          }
        } catch (e) {
          console.error('failed to load role widgets', e);
        }
      }

      // fallback to user widgets
      if (userWidgetsFetching) {
        return;
      }

      if (!userWidgets || userWidgets.length === 0) {
        console.debug('Dashboard: no role layout and userWidgets empty — setting empty list');
        setList([]);
        userListRef.current = [];
        return;
      }

      // Ensure userWidgets is an array; API may return a JSON string or wrapped object.
      let finalUserWidgets = userWidgets;
      if (typeof finalUserWidgets === 'string') {
        finalUserWidgets = safeJSONParse(finalUserWidgets.trim(), []);
      }

      if (
        finalUserWidgets &&
        !Array.isArray(finalUserWidgets) &&
        typeof finalUserWidgets === 'object' &&
        Array.isArray(finalUserWidgets.layout)
      ) {
        finalUserWidgets = finalUserWidgets.layout;
      }

      if (!finalUserWidgets || finalUserWidgets.length === 0) {
        setList([]);
        userListRef.current = [];
        return;
      }

      setList([...finalUserWidgets]);
      userListRef.current = [...finalUserWidgets];
    };

    loadWidgets().catch((e) => console.error(e));
  }, [userWidgets, userWidgetsFetching, perms.data, perms.roleId, perms.isFetching, roleData]);

  useEffect(() => {
    const targetVisibleRows = 2;
    const rowGap = 10;
    const bottomPadding = 24;
    const minRowHeight = 170;
    const settlePassDelays = [0, 40, 120, 260, 500, 900];
    let rafId = null;
    const settleTimers = [];
    let scrollContainer = null;
    let lastObservedGridWidth = null;

    const findScrollContainer = (node) => {
      if (!node || typeof window === 'undefined') {
        return null;
      }

      let current = node.parentElement;
      while (current) {
        const styles = window.getComputedStyle(current);
        const overflowY = styles.overflowY || '';
        const overflow = styles.overflow || '';
        if (/(auto|scroll|overlay)/i.test(`${overflowY} ${overflow}`)) {
          return current;
        }
        current = current.parentElement;
      }

      return null;
    };

    const getStableTopOffset = (node) => {
      const rect = node.getBoundingClientRect();

      if (scrollContainer instanceof HTMLElement) {
        const containerRect = scrollContainer.getBoundingClientRect();
        return rect.top - containerRect.top + scrollContainer.scrollTop;
      }

      return rect.top + (window.scrollY || window.pageYOffset || 0);
    };

    const getViewportHeight = () => {
      if (scrollContainer instanceof HTMLElement) {
        return scrollContainer.clientHeight;
      }

      return window.innerHeight;
    };

    const updateDashboardRowHeight = () => {
      const gridContainer = dashboardGridRef.current;
      if (!gridContainer) {
        return;
      }

      if (!scrollContainer) {
        scrollContainer = findScrollContainer(gridContainer);
      }

      const stableTopOffset = getStableTopOffset(gridContainer);
      const availableHeight = getViewportHeight() - stableTopOffset - bottomPadding;
      if (!Number.isFinite(availableHeight) || availableHeight <= 0) {
        return;
      }

      const calculatedHeight = Math.floor(
        (availableHeight - rowGap * (targetVisibleRows - 1)) / targetVisibleRows,
      );
      const clampedHeight = Math.max(minRowHeight, Math.min(layout.widgetHeight, calculatedHeight));

      setDashboardRowHeight((prev) => (Math.abs(prev - clampedHeight) > 1 ? clampedHeight : prev));
    };

    const scheduleRowHeightUpdate = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateDashboardRowHeight();
      });
    };

    const clearSettledRowHeightUpdates = () => {
      while (settleTimers.length > 0) {
        const timerId = settleTimers.pop();
        window.clearTimeout(timerId);
      }
    };

    const scheduleSettledRowHeightUpdates = () => {
      clearSettledRowHeightUpdates();
      settlePassDelays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          scheduleRowHeightUpdate();
        }, delay);
        settleTimers.push(timerId);
      });
    };

    scheduleRowHeightUpdate();
    scheduleSettledRowHeightUpdates();
    window.addEventListener('resize', scheduleRowHeightUpdate);
    const removeViewportResizeBridge = addViewportResizeListeners(
      () => {
        scheduleSettledRowHeightUpdates();
        window.dispatchEvent(new Event('resize'));
      },
      {
        includeWindowResize: false,
        includeOrientationChange: false,
      },
    );

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        const observedWidth = entries?.[0]?.contentRect?.width;
        if (Number.isFinite(observedWidth)) {
          const roundedWidth = Math.round(observedWidth);
          if (
            lastObservedGridWidth !== null &&
            Math.abs(roundedWidth - lastObservedGridWidth) < 1
          ) {
            return;
          }
          lastObservedGridWidth = roundedWidth;
        }

        scheduleRowHeightUpdate();
        scheduleSettledRowHeightUpdates();
      });

      if (dashboardGridRef.current) {
        resizeObserver.observe(dashboardGridRef.current);
      }
    }

    return () => {
      window.removeEventListener('resize', scheduleRowHeightUpdate);
      removeViewportResizeBridge();
      clearSettledRowHeightUpdates();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [
    canEditWidgets,
    layout.widgetHeight,
    list.length,
    weatherAlertCount,
    fusionAlertCount,
    totalAlarmsCount,
  ]);

  // Stable identity so the memoized widget list below is not invalidated on
  // every Dashboard render (e.g. alarm/alert polling).
  const onListChange = useCallback(
    async (newList) => {
      setList(newList);

      //update user settings
      newList = newList.map((item) => {
        if (!item.w) item = { ...item, w: 1 };
        if (!item.h) item = { ...item, h: 1 };
        if (item.id !== undefined) item = { ...item, id: parseInt(item.id, 10) };
        return item;
      });

      try {
        await updateUserWidgetLayout(newList).unwrap();
      } catch (error) {
        console.error('Failed to update user widget layout');
      }
    },
    [updateUserWidgetLayout],
  );

  const persistGridLayoutChanges = (gridLayout, { includeSize = false } = {}) => {
    const temp = [...list];
    const maxGridWidth = DASHBOARD_COLS_BY_BREAKPOINT.xxl;
    const activeCols = DASHBOARD_COLS_BY_BREAKPOINT[bp] ?? DASHBOARD_COLS_BY_BREAKPOINT.xxl;

    // The saved layout is the desktop arrangement. Single-column (mobile)
    // breakpoints force every widget to x=0/w=1, so nothing done there counts
    // as a user rearrangement — persisting it would collapse the full-screen
    // layout into one column. Mobile reordering stays visual-only and the
    // desktop positions are restored untouched when the viewport grows back.
    if (activeCols <= 1) return;

    for (let i = 0; i < gridLayout.length; i++) {
      const gridItem = gridLayout[i];
      const id = gridItem.i;
      if (id === 'add') continue;

      const idx = parseInt(id, 10);
      if (Number.isNaN(idx) || !temp[idx]) continue;

      const currentItem = temp[idx];
      const widgetDefinition = definedWidgetData?.[currentItem?.id];
      const mapResizable = canWidgetUseExpandedMapSizing(widgetDefinition);
      const chartResizable = canWidgetUseExpandedChartSizing(widgetDefinition);
      const overwatchResizeProfile = getOverwatchWidgetResizeProfile(widgetDefinition, activeCols);
      const connectedDevicesListResizable =
        isConnectedDevicesListWidgetDefinition(widgetDefinition);
      const maxChartWidth = Math.min(4, activeCols);
      const minChartWidth = Math.min(2, activeCols);
      const maxConnectedDevicesListWidth = Math.min(2, activeCols);

      let nextW = includeSize
        ? mapResizable
          ? Math.max(1, Math.min(activeCols || maxGridWidth, Number(gridItem.w) || 1))
          : overwatchResizeProfile
            ? Math.max(
                overwatchResizeProfile.minW,
                Math.min(overwatchResizeProfile.maxW, Number(gridItem.w) || 1),
              )
            : connectedDevicesListResizable
              ? Math.max(1, Math.min(maxConnectedDevicesListWidth, Number(gridItem.w) || 1))
              : chartResizable
                ? Math.max(minChartWidth, Math.min(maxChartWidth, Number(gridItem.w) || 1))
                : (currentItem.w ?? 1)
        : (currentItem.w ?? 1);

      let nextH = includeSize
        ? mapResizable
          ? Math.max(1, Math.min(2, Number(gridItem.h) || 1))
          : overwatchResizeProfile
            ? Math.max(
                overwatchResizeProfile.minH,
                Math.min(overwatchResizeProfile.maxH, Number(gridItem.h) || 1),
              )
            : connectedDevicesListResizable
              ? 2
              : chartResizable
                ? Math.max(1, Math.min(2, Number(gridItem.h) || 1))
                : (currentItem.h ?? 1)
        : (currentItem.h ?? 1);

      if (includeSize && overwatchResizeProfile?.disallowOneByOne && nextW === 1 && nextH === 1) {
        nextH = 2;
      }

      temp[idx] = {
        ...currentItem,
        x: Number.isFinite(gridItem.x) ? gridItem.x : (currentItem.x ?? 0),
        y: Number.isFinite(gridItem.y) ? gridItem.y : (currentItem.y ?? 0),
        ...(includeSize
          ? {
              w: nextW,
              h: nextH,
            }
          : {}),
      };
    }

    onListChange(temp);
  };

  const openWidgetSelector = (value) => {
    setWidgetSelectorOpen(value);
  };

  const addWidgets = (widgets) => {
    if (!Array.isArray(widgets) || !widgets.length) return;

    // Saved positions describe the desktop grid, so placement is always
    // computed against a multi-column grid — adding a widget from the
    // single-column mobile view must not stack it into desktop column 0.
    const breakpointCols = DASHBOARD_COLS_BY_BREAKPOINT[bp] ?? DASHBOARD_COLS_BY_BREAKPOINT.xxl;
    const activeCols = breakpointCols <= 1 ? DASHBOARD_COLS_BY_BREAKPOINT.xxl : breakpointCols;

    const collides = (a, b) =>
      a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

    const canPlaceWidgetAt = (items, candidate) =>
      items.every((existingItem) => !collides(existingItem, candidate));

    const findNextPlacement = (items, w, h) => {
      const normalizedWidth = Math.max(1, Math.min(activeCols, w));
      const maxRow = Math.max(0, ...items.map((item) => item.y + item.h));

      for (let y = 0; y <= maxRow + 100; y++) {
        for (let x = 0; x <= activeCols - normalizedWidth; x++) {
          const candidate = { x, y, w: normalizedWidth, h };
          if (canPlaceWidgetAt(items, candidate)) {
            return { x, y };
          }
        }
      }

      return { x: 0, y: maxRow + 1 };
    };

    const placedWidgets = list.map((item) => ({
      x: Number.isFinite(item?.x) ? item.x : 0,
      y: Number.isFinite(item?.y) ? item.y : 0,
      w: Math.max(1, Math.min(activeCols, Number(item?.w) || 1)),
      h: Math.max(1, Number(item?.h) || 1),
    }));

    const normalizedIncomingWidgets = widgets.map((widget) => {
      const w = Math.max(1, Math.min(activeCols, Number(widget?.w) || 1));
      const h = Math.max(1, Number(widget?.h) || 1);
      const { x, y } = findNextPlacement(placedWidgets, w, h);

      const normalizedWidget = { ...widget, w, h, x, y };
      placedWidgets.push({ x, y, w, h });
      return normalizedWidget;
    });

    return onListChange([...list, ...normalizedIncomingWidgets]);
  };

  const removeWidget = useCallback(
    (toRemove) => {
      onListChange([
        ...list.filter((filter, index) => {
          return index !== toRemove;
        }),
      ]);
    },
    [list, onListChange],
  );

  const removeAllWidgets = () => {
    if (!Array.isArray(list) || list.length === 0) {
      setShowRemoveAllConfirm(false);
      return;
    }

    if (canOverrideLockedFromRole) {
      onListChange([]);
      setShowRemoveAllConfirm(false);
      return;
    }

    onListChange(list.filter((item) => item?.lockedFromRole === true));
    setShowRemoveAllConfirm(false);
  };

  useEffect(() => {
    if (locationPermissionNotice) {
      toast.success(locationPermissionNotice);
    }
  }, [locationPermissionNotice]);

  const removeAllWidgetsInlineAction = (
    <button
      type="button"
      className="bg-transparent border-0 p-0"
      disabled={removableWidgetCount === 0}
      onClick={() => setShowRemoveAllConfirm(true)}
      title={
        lockedRoleWidgetCount > 0
          ? 'Remove all removable widgets (role-locked widgets will remain)'
          : 'Remove all widgets'
      }
      aria-label="Remove all widgets"
      style={{
        color: 'var(--color-font)',
        fontSize: 11,
        lineHeight: 1,
        opacity: removableWidgetCount === 0 ? 0.45 : 0.8,
        textDecoration: 'none',
        cursor: removableWidgetCount === 0 ? 'not-allowed' : 'pointer',
      }}
    >
      Remove All Widgets
    </button>
  );

  // Memoized so the widget subtrees only rebuild when something that actually
  // affects them changes (list/layout/sizing/perms/search), not on every
  // Dashboard render caused by alarm/alert polling and other header state.
  const widgetChildren = useMemo(() => {
    if (!list) return null;
    return list.map((item, index) => {
      const currentBreakpointLayout = layouts?.[bp] || [];
      const currentLayoutItem = currentBreakpointLayout.find(
        (layoutItem) => layoutItem.i === String(index),
      );
      const resolvedW = Number(currentLayoutItem?.w) || Number(item?.w) || 1;
      const resolvedH = Number(currentLayoutItem?.h) || Number(item?.h) || 1;
      const widgetOnSmallScreen = ['xs', 'sm', 'md'].includes(bp) && resolvedW > 1;
      if (widgetOnSmallScreen) {
        return null;
      }
      const widgetDefinition = definedWidgetData[item.id];

      if (!widgetDefinition) {
        return null;
      }
      const widgetSizeKey = `${resolvedW}x${resolvedH}`;

      const activeCols = DASHBOARD_COLS_BY_BREAKPOINT[bp] ?? DASHBOARD_COLS_BY_BREAKPOINT.xxl;
      const allowExpandedMapSize =
        canWidgetUseExpandedMapSizing(widgetDefinition) &&
        resolvedW >= 1 &&
        resolvedW <= activeCols &&
        resolvedH >= 1 &&
        resolvedH <= 2;
      const allowExpandedChartSize =
        canWidgetUseExpandedChartSizing(widgetDefinition) &&
        resolvedW >= Math.min(2, activeCols) &&
        resolvedW <= Math.min(4, activeCols) &&
        resolvedH >= 1 &&
        resolvedH <= 2;
      const allowExpandedConnectedDevicesListSize =
        isConnectedDevicesListWidgetDefinition(widgetDefinition) &&
        resolvedW >= 1 &&
        resolvedW <= Math.min(2, activeCols) &&
        resolvedH === 2;
      const overwatchResizeProfile = getOverwatchWidgetResizeProfile(widgetDefinition, activeCols);
      const allowExpandedOverwatchWidgetSize =
        Boolean(overwatchResizeProfile) &&
        resolvedW >= overwatchResizeProfile.minW &&
        resolvedW <= overwatchResizeProfile.maxW &&
        resolvedH >= overwatchResizeProfile.minH &&
        resolvedH <= overwatchResizeProfile.maxH &&
        !(overwatchResizeProfile.disallowOneByOne && resolvedW === 1 && resolvedH === 1);
      const showResizeIndicator =
        canEditWidgets &&
        (item.isDraggable ?? true) &&
        (canWidgetUseExpandedMapSizing(widgetDefinition) ||
          canWidgetUseExpandedChartSizing(widgetDefinition) ||
          isConnectedDevicesListWidgetDefinition(widgetDefinition) ||
          Boolean(overwatchResizeProfile));
      if (
        widgetDefinition.sizes &&
        !widgetDefinition.sizes[widgetSizeKey] &&
        !allowExpandedMapSize &&
        !allowExpandedChartSize &&
        !allowExpandedConnectedDevicesListSize &&
        !allowExpandedOverwatchWidgetSize
      ) {
        return null;
      }
      if (!widgetDefinition.sidebarData?.accessLevel) {
        console.warn('Widget definition missing sidebarData.assessLevel', widgetDefinition);
        return null;
      }
      if (!widgetDefinition.sidebarData.accessLevel.includes(accessLevel)) return null;

      if (item?.displayed === false || item?.active === false) return null;

      if (
        !widgetDefinition.name
          .replace(' ', '')
          .toLowerCase()
          .includes(search.replace(' ', '').toLowerCase())
      )
        return null;

      if (search !== '') {
        return (
          <div key={String(index)}>
            <Widget
              key={String(index)}
              item={{
                ...widgetDefinition,
                ...item,
                w: resolvedW,
                h: resolvedH,
              }}
              index={index}
              expandable
              list={list}
              onListChange={onListChange}
              removeWidget={removeWidget}
              lockedFromRole={item?.lockedFromRole || false}
              gridRowHeight={dashboardRowHeight}
              showResizeIndicator={showResizeIndicator}
              isPopoutEnabled={['lg', 'xl', 'xxl'].includes(bp)}
            />
          </div>
        );
      } else {
        return (
          <div key={String(index)}>
            <Widget
              key={String(index)}
              item={{ ...widgetDefinition, ...item, w: resolvedW, h: resolvedH }}
              index={index}
              expandable
              list={list}
              onListChange={onListChange}
              removeWidget={removeWidget}
              lockedFromRole={item?.lockedFromRole || false}
              gridRowHeight={dashboardRowHeight}
              showResizeIndicator={showResizeIndicator}
              isPopoutEnabled={['lg', 'xl', 'xxl'].includes(bp)}
            />
          </div>
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    list,
    bp,
    layouts,
    definedWidgetData,
    search,
    accessLevel,
    canEditWidgets,
    dashboardRowHeight,
    onListChange,
    removeWidget,
  ]);

  return (
    <>
      <Container fluid className="pt-0">
        {/* Header */}
        <p className="fs-6 text-center text-md-start mb-2">
          Welcome back, {username}, glad to see you again!
        </p>
        {/* Header widgets */}
        {showAnalyticHeaders && (
          <div className="row w-100 mt-0 g-3">
            <div className="col-12 col-xl-3">
              <TotalAlertCard alertCount={weatherAlertCount} title="Weather Alerts" />
            </div>
            <div className="col-12 col-xl-3">
              <TotalAlertCard alertCount={fusionAlertCount} title="Fusion Alerts" />
            </div>
            <div className="col-12 col-xl-3">
              <TotalAlertCard alertCount={'Coming Soon'} title="AI Alerts" />
            </div>
            <div className="col-12 col-xl-3">
              <TotalAlertCard
                alertCount={totalAlarmsCount}
                title="Pending Alarms"
                navLink={canViewAlarms ? '/overwatch/pendingalarms' : ''}
              />
            </div>
          </div>
        )}
        {canEditWidgets && (
          <>
            {showAnalyticHeaders ? (
              <div className="row w-100 gx-3 gy-0 mt-1 mb-0">
                <div
                  className="col-12 col-xl-3 offset-xl-9 d-flex justify-content-end align-items-center"
                  style={{ minHeight: 14 }}
                >
                  {removeAllWidgetsInlineAction}
                </div>
              </div>
            ) : (
              <div
                className="d-flex justify-content-end align-items-center mt-1 mb-0"
                style={{ minHeight: 14 }}
              >
                {removeAllWidgetsInlineAction}
              </div>
            )}
          </>
        )}
        {/* Widgets */}
        <div className="w-100" ref={dashboardGridRef}>
          <ResponsiveGridLayout
            // Bootstrap breakpoints
            breakpoints={{
              xs: 0,
              sm: 576,
              md: 768,
              lg: 992,
              xl: 1200,
              xxl: 1400,
            }}
            cols={{ xs: 1, sm: 1, md: 1, lg: 3, xl: 4, xxl: 4 }}
            rowHeight={dashboardRowHeight}
            margin={[10, 10]}
            layouts={layouts}
            isResizable={canEditWidgets}
            onBreakpointChange={(newBp) => setBp(newBp)}
            onDragStop={(gridLayout) =>
              persistGridLayoutChanges(gridLayout, { includeSize: false })
            }
            onResizeStop={(gridLayout) =>
              persistGridLayoutChanges(gridLayout, { includeSize: true })
            }
            draggableHandle=".handle"
            style={{
              alignItems: 'start',
              marginLeft: '-10px',
            }}
          >
            {widgetChildren}
            {canEditWidgets && (
              <div
                key="add"
                className="rounded-2 overflow-hidden"
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'var(--color-base)',
                }}
              >
                <AddWidgetComponent openWidgetSelector={() => openWidgetSelector(true)} />
              </div>
            )}
          </ResponsiveGridLayout>
        </div>
      </Container>
      {choiceList && widgetSelectorOpen && (
        <WidgetSelector
          widgetData={definedWidgetData}
          userList={typeof choiceList == 'string' ? JSON.parse(choiceList) : choiceList}
          show={widgetSelectorOpen}
          handleShow={() => openWidgetSelector(false)}
          onAddWidgets={addWidgets}
        />
      )}
      <ConfirmationModal
        show={showRemoveAllConfirm}
        handleShow={() => setShowRemoveAllConfirm(false)}
        bodyTxt={
          lockedRoleWidgetCount > 0
            ? 'Remove all removable widgets from the dashboard? Role-locked widgets will remain.'
            : 'Remove all widgets from the dashboard?'
        }
        cancelBtnTxt="Cancel"
        confirmBtnTxt="Remove All"
        cancelHandler={() => setShowRemoveAllConfirm(false)}
        confirmHandler={removeAllWidgets}
      />
    </>
  );
}
